import { useRef, useEffect, useState, useCallback } from 'react'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { useTranslation } from 'react-i18next'
import { Paperclip, Send, Image, X, AlertCircle, RotateCw } from 'lucide-react'
import { ArtistsIcon } from '@/lib/navigationItems'
import { ArtistContentSelector } from './ArtistContentSelector'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { CHAT_COMBINED_ATTACHMENT_LIMIT } from '@/lib/chatData'
import { cn } from '@/lib/utils'
import type { PortfolioItem, PendingImage } from '@/types/models'

interface ChatInputProps {
  /** Mount attachment sheet portal inside this node so taps aren’t stolen by body-level stacking (mobile). */
  attachmentSheetPortalHostRef?: React.RefObject<HTMLElement | null>
  message: string
  setMessage: (message: string) => void
  pendingImages: PendingImage[]
  onSendMessage: () => Promise<void>
  onImageSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveImage: (clientId: string) => void
  onRetryImageUpload?: (clientId: string) => void
  disabled?: boolean
  keyboardVisible?: boolean
  onOffPlatformWarning?: (callback: () => Promise<void>, matchedKeyword: string) => void
  artistId?: string
  artistDisplayName?: string
  initialFlashId?: string | null
  /** When true, attachment / image UI is disabled (e.g. text-only support chat). */
  attachmentsDisabled?: boolean
  attachedArtistPortfolioItems?: PortfolioItem[]
  replaceAttachedArtistPortfolioSelection?: (items: PortfolioItem[]) => void
  removeAttachedArtistPortfolioItemById?: (itemId: string) => void
  artistSheetReferenceFirstName?: string | null
  /** Optional WhatsApp-style reply preview strip (shown above the composer row). */
  replyComposerPreview?: { authorLabel: string; bodyPreview: string } | null
  onDismissReplyComposerPreview?: () => void
  /** When true on native, focus the composer on mount (and when this flips true, e.g. after auth). */
  autoFocusComposer?: boolean
}

export function ChatInput({
  attachmentSheetPortalHostRef,
  message,
  setMessage,
  pendingImages,
  onSendMessage,
  onImageSelect,
  onRemoveImage,
  onRetryImageUpload,
  disabled = false,
  keyboardVisible = false,
  onOffPlatformWarning,
  artistId,
  artistDisplayName,
  initialFlashId,
  attachmentsDisabled = false,
  attachedArtistPortfolioItems = [],
  replaceAttachedArtistPortfolioSelection = () => {},
  removeAttachedArtistPortfolioItemById = () => {},
  artistSheetReferenceFirstName,
  replyComposerPreview,
  onDismissReplyComposerPreview,
  autoFocusComposer = true,
}: ChatInputProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showAttachmentOptions, setShowAttachmentOptions] = useState(false)
  const [showArtistContentSelector, setShowArtistContentSelector] = useState(false)

  console.log('[ChatInput] Rendered with props:', {
    artistId,
    artistDisplayName,
    hasMessage: !!message.trim()
  })

  // Keywords to detect off-platform communication
  // Matches any word containing "instagram", "whatsapp", or standalone "ig"
  const PLATFORM_KEYWORDS = /(instagram\w*|whatsapp\w*|\big\b)/gi

  // Calculate Levenshtein distance between two strings
  const getLevenshteinDistance = (str1: string, str2: string): number => {
    const lower1 = str1.toLowerCase()
    const lower2 = str2.toLowerCase()
    
    if (lower1.length === 0) return lower2.length
    if (lower2.length === 0) return lower1.length
    
    const matrix: number[][] = []
    
    for (let i = 0; i <= lower2.length; i++) {
      matrix[i] = [i]
    }
    for (let j = 0; j <= lower1.length; j++) {
      matrix[0][j] = j
    }
    
    for (let i = 1; i <= lower2.length; i++) {
      for (let j = 1; j <= lower1.length; j++) {
        if (lower2.charAt(i - 1) === lower1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          )
        }
      }
    }
    
    return matrix[lower2.length][lower1.length]
  }

  // Add this function to handle auto-growing
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current
    if (!textarea) return
    
    // Reset height to min to accurately calculate scroll height
    textarea.style.height = '44px'
    
    // Calculate new height (capped at max 84px - 3 lines)
    const newHeight = Math.min(textarea.scrollHeight, 84)
    textarea.style.height = `${newHeight}px`
  }

  // Add effect to handle message changes
  useEffect(() => {
    adjustTextareaHeight()
  }, [message])

  const focusComposer = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea || disabled) return

    textarea.focus({ preventScroll: true })

    if (Capacitor.getPlatform() === 'android') {
      void Keyboard.show().catch(() => {})
    }
  }, [disabled])

  // Auto-open keyboard when entering chat on native (skip when flash picker opens instead).
  useEffect(() => {
    if (!autoFocusComposer || disabled || !Capacitor.isNativePlatform()) return
    if (initialFlashId && artistId && !attachmentsDisabled) return

    const delayMs = 150
    const timerId = window.setTimeout(() => focusComposer(), delayMs)
    return () => window.clearTimeout(timerId)
  }, [autoFocusComposer, disabled, initialFlashId, artistId, attachmentsDisabled, focusComposer])

  useEffect(() => {
    if (!replyComposerPreview || disabled) return
    focusComposer()
  }, [replyComposerPreview, disabled, focusComposer])

  useEffect(() => {
    if (pendingImages.length > 0 || attachedArtistPortfolioItems.length > 0) {
      setShowAttachmentOptions(false)
    }
  }, [pendingImages, attachedArtistPortfolioItems])

  const combinedAttachmentsCount = pendingImages.length + attachedArtistPortfolioItems.length
  const attachmentsAtMaximumCombinedCapacity =
    combinedAttachmentsCount >= CHAT_COMBINED_ATTACHMENT_LIMIT

  const trimmedArtistFirstNameForAttachSheet = artistSheetReferenceFirstName?.trim()
  const artistReferenceSheetExplanation = trimmedArtistFirstNameForAttachSheet
    ? t('chat.referenceAttachHintNamed', { name: trimmedArtistFirstNameForAttachSheet })
    : t('chat.referenceAttachHintGeneric')

  // Auto-open content selector if initialFlashId is provided
  useEffect(() => {
    if (attachmentsDisabled) return
    if (initialFlashId && artistId && !showArtistContentSelector) {
      setShowAttachmentOptions(false)
      setShowArtistContentSelector(true)
    }
  }, [initialFlashId, artistId, showArtistContentSelector, attachmentsDisabled])

  const handleArtistReferenceSheetOpenChange = (isOpen: boolean) => {
    setShowArtistContentSelector(isOpen)
    if (!isOpen) {
      setShowAttachmentOptions(false)
    }
  }

  const handleImageButtonClick = () => {
    if (attachmentsDisabled) return
    // If artist content selector is open, close everything
    if (showArtistContentSelector) {
      handleArtistReferenceSheetOpenChange(false)
      return
    }
    // Otherwise, toggle attachment options
    setShowAttachmentOptions(!showAttachmentOptions)
  }

  const handleAttachFromDevice = () => {
    try {
      setErrorMessage(null)
      fileInputRef.current?.click()
      setShowAttachmentOptions(false)
    } catch (error) {
      console.error('[ChatInput] Error opening file picker:', error)
      setErrorMessage(t('chat.imagePickerError') || 'Failed to open image picker')
    }
  }

  const handleAttachFromArtist = () => {
    const maximumSelectablePortfolioSlotsRemaining = Math.max(
      0,
      CHAT_COMBINED_ATTACHMENT_LIMIT -
        pendingImages.length -
        attachedArtistPortfolioItems.length
    )
    if (maximumSelectablePortfolioSlotsRemaining <= 0) {
      return
    }
    setShowAttachmentOptions(false)
    setShowArtistContentSelector(true)
  }

  // Check if any images are currently uploading or have errors
  const hasUploadingImages = pendingImages.some(img => img.status === 'uploading')
  const hasErrorImages = pendingImages.some(img => img.status === 'error')
  const readyImages = pendingImages.filter(img => img.status === 'sent')

  const handleSend = async () => {
    console.log('[ChatInput] handleSend called', {
      hasMessage: !!message.trim(),
      messageLength: message.length,
      hasPendingImages: pendingImages.length > 0,
      hasAttachedArtistPortfolioItems: attachedArtistPortfolioItems.length > 0,
      disabled,
      uploadingImages: hasUploadingImages,
      errorImages: hasErrorImages
    })
    
    const hasAttachedPortfolioReferencesInStrip = attachedArtistPortfolioItems.length > 0
    
    if (!message.trim() && !readyImages.length && !hasAttachedPortfolioReferencesInStrip) {
      console.log('[ChatInput] No message, ready images, or portfolio references, returning early')
      return
    }
    if (hasUploadingImages) {
      console.log('[ChatInput] Images currently uploading, blocking new send')
      return
    }
    if (hasErrorImages) {
      console.log('[ChatInput] Some images failed to upload, user must fix before sending')
      return
    }
    
    handleArtistReferenceSheetOpenChange(false)
    
    console.log('[ChatInput] Proceeding with send...')
    
    let matchedKeyword: string | null = null
    
    const platformKeywordMatch = message.match(PLATFORM_KEYWORDS)
    if (platformKeywordMatch) {
      matchedKeyword = platformKeywordMatch[0].toLowerCase()
    }
    
    // Check if artistDisplayName appears in the message (case-insensitive)
    if (!matchedKeyword && artistDisplayName) {
      const displayNameRegex = new RegExp(`\\b\\S+\\b`, 'gi')
      const words = message.match(displayNameRegex) || []
      
      for (const word of words) {
        if (word.toLowerCase() === artistDisplayName.toLowerCase()) {
          matchedKeyword = word.toLowerCase()
          break
        }
        
        const distance = getLevenshteinDistance(word, artistDisplayName)
        if (distance === 1 && word.length >= 3) {
          matchedKeyword = word.toLowerCase()
          break
        }
      }
      
      console.log('[ChatInput] displayName check:', {
        artistDisplayName,
        message,
        matchedKeyword,
        wordsFound: words
      })
    }
    
    if (matchedKeyword) {
      console.log('[ChatInput] Off-platform flag detected:', matchedKeyword)
      if (onOffPlatformWarning) {
        onOffPlatformWarning(async () => {
          await onSendMessage()
        }, matchedKeyword)
      } else {
        await onSendMessage()
      }
      return
    }
    
    console.log('[ChatInput] Calling onSendMessage...')
    await onSendMessage()
    console.log('[ChatInput] onSendMessage completed')
  }

  const handleImageSelectWithErrorHandling = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setErrorMessage(null)
      onImageSelect(event)
      // Reset the input value to allow selecting the same file again
      if (event.target) {
        event.target.value = ''
      }
    } catch (error) {
      console.error('[ChatInput] Error selecting image:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to select image. Please try again.'
      setErrorMessage(errorMessage)
      // Reset the input value
      if (event.target) {
        event.target.value = ''
      }
    }
  }

  // Disable send if:
  // 1. No content to send
  // 2. Component is disabled
  // 3. Images are currently being uploaded (let them finish before allowing new send)
  // 4. Any images have errors (user must retry or remove them)
  const canSend =
    (message.trim().length > 0 || readyImages.length > 0 || attachedArtistPortfolioItems.length > 0) &&
    !disabled &&
    !hasUploadingImages &&
    !hasErrorImages

  console.log('[ChatInput] Render - canSend:', canSend, {
    hasContent: !!(
      message.trim() ||
      readyImages.length ||
      attachedArtistPortfolioItems.length > 0
    ),
    disabled,
    hasUploadingImages,
    hasErrorImages,
    pendingImageStatuses: pendingImages.map(img => img.status),
    attachedArtistPortfolioItemsCount: attachedArtistPortfolioItems.length,
    readyImagesCount: readyImages.length
  })

  return (
    <div 
      className="bg-transparent px-4 relative overflow-visible"
      style={{
        paddingTop: '0.75rem',
        paddingBottom: keyboardVisible ? '0.5rem' : '2rem',
        transition: 'padding-bottom 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Full-bleed top shadow when artist content selector is open */}
      {showArtistContentSelector && artistId && !attachmentsDisabled && (
        <div 
          className="absolute top-0 h-8 pointer-events-none -translate-y-full"
          style={{ 
            left: '-4rem',
            right: '-4rem',
            background: 'linear-gradient(to top, rgba(0, 0, 0, 0.3), transparent)'
          }}
        />
      )}
      {replyComposerPreview && onDismissReplyComposerPreview && (
        <div className="relative mb-2 flex min-h-[3rem] items-stretch overflow-hidden rounded-[var(--card-radius)] border border-border bg-muted pr-1">
          <span
            className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-primary"
            aria-hidden
          />
          <div className="relative z-[1] flex min-w-0 flex-1 flex-col justify-center py-2 pl-3 pr-1">
            <p className="text-xs font-semibold text-primary leading-tight">
              {replyComposerPreview.authorLabel}
            </p>
            <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-muted-foreground">
              {replyComposerPreview.bodyPreview}
            </p>
          </div>
          <button
            type="button"
            onClick={onDismissReplyComposerPreview}
            className="relative z-[1] flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={t('chat.replyDismiss')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-3 p-3 bg-red-100 border border-red-300 rounded-[var(--card-radius)] text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">Image Upload Error</p>
            <p className="text-xs mt-1">{errorMessage}</p>
          </div>
          <button
            onClick={() => setErrorMessage(null)}
            className="text-red-600 hover:text-red-800 font-medium text-xs flex-shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Selected attachments: artist flashes/tattoos + photo uploads (shared combined cap) */}
      {!attachmentsDisabled &&
        (attachedArtistPortfolioItems.length > 0 || pendingImages.length > 0) && (
        <div className="mb-2 flex gap-2 flex-wrap">
          {attachedArtistPortfolioItems.map(portfolioReferenceItem => (
            <div key={`portfolio-ref-${portfolioReferenceItem.id}`} className="relative inline-block">
              <img
                src={portfolioReferenceItem.compressedImageUrl || portfolioReferenceItem.imageUrl}
                alt=""
                className="w-14 h-14 object-cover rounded-[var(--card-radius)] ring-1 ring-border/50"
              />
              <button
                type="button"
                onClick={() => removeAttachedArtistPortfolioItemById(portfolioReferenceItem.id)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 text-sm leading-none bg-background text-muted-foreground rounded-full flex items-center justify-center hover:bg-muted transition-colors shadow-sm border border-border z-10"
              >
                ×
              </button>
            </div>
          ))}
          {pendingImages
            .slice(
              0,
              Math.max(0, CHAT_COMBINED_ATTACHMENT_LIMIT - attachedArtistPortfolioItems.length)
            )
            .map(img => (
            <div key={img.clientId} className="relative inline-block">
              {img.status === 'uploading' ? (
                <div className="w-14 h-14 bg-muted rounded-[var(--card-radius)] relative overflow-hidden">
                  <div className="absolute inset-0 shimmer-gradient" 
                       style={{
                         backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
                         backgroundSize: '200% 100%',
                         animation: 'shimmer 3s infinite'
                       }}
                  />
                </div>
              ) : (
                <img
                  src={img.localPreviewUrl}
                  alt="Selected image"
                  className="w-14 h-14 object-cover rounded-[var(--card-radius)]"
                />
              )}
              
              {img.status === 'error' && (
                <div 
                  className="absolute inset-0 bg-red-500/40 rounded-[var(--card-radius)] flex items-center justify-center cursor-help group"
                  title={img.error || 'Upload failed'}
                >
                  <AlertCircle className="w-4 h-4 text-white" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                    {img.error || 'Upload failed'}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => onRemoveImage(img.clientId)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 text-sm leading-none bg-background text-muted-foreground rounded-full flex items-center justify-center hover:bg-muted transition-colors shadow-sm border border-border z-10"
              >
                ×
              </button>

              {img.status === 'error' && onRetryImageUpload && (
                <button
                  type="button"
                  onClick={() => onRetryImageUpload(img.clientId)}
                  className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-red-500 text-white text-[9px] rounded-full flex items-center gap-0.5 hover:bg-red-600 transition-colors shadow-sm z-10"
                >
                  <RotateCw className="w-2.5 h-2.5" />
                  Retry
                </button>
              )}
            </div>
          ))}
        </div>
        )}

      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Bottom sheet — flash / tattoo picker */}
      {artistId && !attachmentsDisabled && (
        <Sheet open={showArtistContentSelector} onOpenChange={handleArtistReferenceSheetOpenChange}>
          <SheetContent
            side="bottom"
            hideCloseButton
            portalContainer={attachmentSheetPortalHostRef?.current ?? undefined}
            overlayClassName="z-[47]"
            className={cn(
              // Below chat chrome (input bar is z-50) so attach / close controls stay tappable
              'z-[48] rounded-t-2xl border-t border-border p-0 gap-0 max-h-[85vh] overflow-hidden'
            )}
          >
            <ArtistContentSelector
              artistId={artistId}
              onSelect={replaceAttachedArtistPortfolioSelection}
              selectedItems={attachedArtistPortfolioItems}
              maxSelection={Math.max(0, CHAT_COMBINED_ATTACHMENT_LIMIT - pendingImages.length)}
              referenceExplanationText={artistReferenceSheetExplanation}
              initialFlashId={initialFlashId}
            />
          </SheetContent>
        </Sheet>
      )}
      
      {/* Input Row */}
      <div className="flex gap-2">
        {/* Attachment Button - Changes to X when selector or options are open */}
        <div className="shrink-0">
          <button
            onClick={handleImageButtonClick}
            disabled={
              attachmentsDisabled ||
              disabled ||
              (!showArtistContentSelector &&
                !showAttachmentOptions &&
                attachmentsAtMaximumCombinedCapacity)
            }
            data-testid="attachment-button"
            className={`aspect-square flex items-center justify-center rounded-[var(--card-radius)] transition-all ${
              showArtistContentSelector || showAttachmentOptions
                ? 'bg-muted text-foreground hover:bg-muted/80'
                : attachmentsDisabled ||
                    disabled ||
                    attachmentsAtMaximumCombinedCapacity
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
            style={{ height: '44px' }}
            title={showArtistContentSelector || showAttachmentOptions ? 'Close' : 'Attach'}
          >
            {showArtistContentSelector || showAttachmentOptions ? (
              <X className="w-4 h-4" />
            ) : (
              <Paperclip className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Text Input */}
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('chat.typePlaceholder')}
            disabled={disabled}
            rows={1}
            className="w-full min-h-[44px] max-h-[84px] px-4 py-3 bg-muted rounded-[var(--card-radius)] border-0 focus:outline-none resize-none text-[15px] leading-[20px] placeholder:text-muted-foreground overflow-y-auto text-foreground"
            style={{
              scrollbarWidth: 'thin',
              msOverflowStyle: 'scrollbar'
            }}
          />
        </div>

        {/* Send Button */}
        <div className="shrink-0">
          <button
            onClick={handleSend}
            disabled={!canSend}
            className={`aspect-square flex items-center justify-center rounded-[var(--card-radius)] transition-all ${
              canSend 
                ? 'bg-primary hover:bg-primary/90 text-primary-foreground' 
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
            style={{ height: '44px' }} // Same height as chat bubble
          >
            <Send className="w-4 h-4" /> {/* Slightly smaller icon */}
          </button>
        </div>
        
        {!attachmentsDisabled && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/heic,image/heif,image/*"
          multiple
          onChange={handleImageSelectWithErrorHandling}
          className="hidden"
        />
        )}
      </div>

      {/* Attachment Options - Below input row */}
      {showAttachmentOptions && !attachmentsDisabled && (
        <div className="mt-3 flex gap-3 animate-in slide-in-from-bottom-2 duration-200">
          {/* Attach from Device */}
          <button
            onClick={handleAttachFromDevice}
            disabled={disabled || attachmentsAtMaximumCombinedCapacity}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-[var(--card-radius)] transition-all ${
              disabled || attachmentsAtMaximumCombinedCapacity
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            <Image className="w-4 h-4" />
            <span className="text-sm font-medium">Photos</span>
          </button>

          {/* Attach from Artist */}
          <button
            onClick={handleAttachFromArtist}
            disabled={disabled || !artistId || attachmentsAtMaximumCombinedCapacity}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-[var(--card-radius)] transition-all ${
              disabled || !artistId || attachmentsAtMaximumCombinedCapacity
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            <ArtistsIcon className="w-4 h-4" />
            <span className="text-sm font-medium">From Artist</span>
          </button>
        </div>
      )}
    </div>
  )
}